import { useMemo } from 'react';
import { useChatStore } from '@auw/stores';
import type { ConversationRuntimeState } from '@auw/stores';
import type { Conversation, WorkspaceMode } from '@auw/workspace-utils';
import {
  normalizeComposerWorkspaceMode,
  useWorkspaceRoute,
} from '../../workspace/navigation/useWorkspaceRoute';

export interface ComposerTargetState {
  workspaceMode: WorkspaceMode;
  routeConversationId: string | null;
  conversation: Conversation | null;
  runtimeConversationId: string | null;
  runtime: ConversationRuntimeState | null;
  isStreaming: boolean;
  isUploading: boolean;
}

export function useComposerTarget(modeOverride?: WorkspaceMode): ComposerTargetState {
  const route = useWorkspaceRoute();
  const fallbackMode = useChatStore((s) => s.activeMode);
  const workspaceMode = useMemo(
    () => normalizeComposerWorkspaceMode(modeOverride ?? route.mode ?? fallbackMode),
    [fallbackMode, modeOverride, route.mode],
  );
  const routeConversationId = route.itemId;
  const conversation = useChatStore((s) => {
    if (!routeConversationId) return null;
    const current = s.conversations[routeConversationId] ?? null;
    if (!current) return null;
    if (current.mode !== workspaceMode) return null;
    if (current.workspaceKind !== 'page') return null;
    return current;
  });
  const runtimeConversationId = conversation?.id ?? null;
  const runtime = useChatStore((s) => (
    runtimeConversationId ? (s.conversationRuntimes[runtimeConversationId] ?? null) : null
  ));
  const isStreaming = useChatStore((s) => s.isStreaming(runtimeConversationId));
  const isUploading = useChatStore((s) => s.isUploadingConversation(runtimeConversationId));

  return {
    workspaceMode,
    routeConversationId,
    conversation,
    runtimeConversationId,
    runtime,
    isStreaming,
    isUploading,
  };
}
